import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Billing Report Diff page. Three views switched by clicking the
// three stat tiles at the top:
//   missing      — billing rows NOT in Command (default)
//   billing_all  — every billing-report row (with in_command flag)
//   local_only   — Active Command sites not in the billing report
//
// Matching falls back to digit-only when the alpha prefix differs
// (e.g. billing 'ess50050' vs local 'pki50050'). Fuzzy matches get a
// 'fuzzy' badge so you can see when this kicked in.

function PosBadge({ pos }) {
  if (!pos) return null;
  const p = String(pos).toUpperCase();
  const tone = p.includes('ESSO')     ? { bg: '#fee2e2', fg: '#991b1b' }
             : p.includes('SUNCOR') || p.includes('PETRO') ? { bg: '#ffedd5', fg: '#9a3412' }
             : p.includes('PASSPORT') ? { bg: '#ede9fe', fg: '#5b21b6' }
             : p.includes('BULLOCH')  ? { bg: '#dbeafe', fg: '#1e40af' }
             : { bg: '#f3f4f6', fg: '#6b7280' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px',
      background: tone.bg, color: tone.fg,
      borderRadius: 'var(--radius-sm, 4px)',
      fontFamily: 'var(--font-mono)', fontSize: '10.5px',
      fontWeight: 'var(--font-semibold)', letterSpacing: '0.04em'
    }}>{pos}</span>
  );
}

function Tile({ label, value, tone, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '12px 22px',
        borderRadius: 'var(--radius-lg)',
        border: `2px solid ${tone}`,
        background: active ? tone : 'var(--color-surface)',
        color: active ? '#fff' : 'var(--color-text)',
        cursor: 'pointer', textAlign: 'left', minWidth: 160,
        transition: 'background 0.12s, color 0.12s',
        fontFamily: 'inherit'
      }}
    >
      <div style={{
        fontSize: 'var(--text-caption)',
        fontWeight: 'var(--font-semibold)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        opacity: 0.9
      }}>{label}</div>
      <div style={{
        fontSize: '28px',
        fontFamily: 'var(--font-mono)',
        fontWeight: 'var(--font-bold)',
        lineHeight: 1.1, marginTop: 2,
        color: active ? '#fff' : tone
      }}>{value}</div>
    </button>
  );
}

function FuzzyBadge({ localSiteNumber }) {
  return (
    <span
      title={`Matched by digits — local site_number is "${localSiteNumber}"`}
      style={{
        marginLeft: 6,
        padding: '1px 6px',
        background: '#fef3c7',
        color: '#b45309',
        borderRadius: 'var(--radius-sm)',
        fontFamily: 'var(--font-mono)',
        fontSize: '10px',
        fontWeight: 'var(--font-semibold)'
      }}
    >fuzzy</span>
  );
}

export default function BillingReportDiff() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [view, setView] = useState('missing');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/billing-report/diff')
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const sourceList = useMemo(() => {
    if (!data) return [];
    if (view === 'billing_all') return data.billing_all || [];
    if (view === 'local_only')  return data.local_only  || [];
    return data.missing || [];
  }, [data, view]);

  const visible = useMemo(() => {
    if (!sourceList.length) return [];
    const q = query.trim().toLowerCase();
    if (!q) return sourceList;
    return sourceList.filter((r) => {
      const fields = view === 'local_only'
        ? [r.site_number, r.site_name, r.company, r.city, r.province_state, r.pos_type]
        : [r.unique_site_id, r.client_id, r.site_name, r.city, r.province, r.pos, r.ci_si];
      const hay = fields.map((v) => v == null ? '' : String(v).toLowerCase()).join(' | ');
      return hay.includes(q);
    });
  }, [sourceList, query, view]);

  // Delete a local site directly from the local_only view. Updates
  // the in-memory list so the row disappears without a full refetch.
  async function handleDelete(site) {
    const label = site.site_name || site.site_number || `site #${site.site_id}`;
    if (!window.confirm(`Delete ${label} from Command?\n\nThis removes the site row and any notes/credentials/subscriptions attached to it. Cannot be undone.`)) return;
    setDeletingId(site.site_id);
    try {
      await axios.delete(`/api/sites/${site.site_id}`);
      setData((prev) => prev && {
        ...prev,
        local_only:       prev.local_only.filter((s) => s.site_id !== site.site_id),
        local_total:      prev.local_total - 1,
        local_only_total: prev.local_only_total - 1
      });
    } catch (err) {
      alert(`Delete failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setDeletingId(null);
    }
  }

  // Remove a row from the billing-report JSON. Used on the Missing
  // and All views to permanently dismiss a billing-report entry that
  // Dave knows is stale (e.g. xsiteio still bills for a retired site).
  async function handleDeleteBillingRow(row) {
    const label = row.site_name || row.unique_site_id;
    if (!window.confirm(`Remove "${label}" from the billing report list?\n\nThis edits server/data/billing-report-2026.json on disk and the row stops appearing here. Permanent until the next billing-report import.`)) return;
    setDeletingId(row.unique_site_id);
    try {
      await axios.delete(`/api/billing-report/row/${encodeURIComponent(row.unique_site_id)}`);
      setData((prev) => {
        if (!prev) return prev;
        const wasMissing = prev.missing.some((m) => m.unique_site_id === row.unique_site_id);
        return {
          ...prev,
          billing_total: prev.billing_total - 1,
          missing_total: prev.missing_total - (wasMissing ? 1 : 0),
          billing_all:   prev.billing_all.filter((m) => m.unique_site_id !== row.unique_site_id),
          missing:       prev.missing.filter((m) => m.unique_site_id !== row.unique_site_id)
        };
      });
    } catch (err) {
      alert(`Delete failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setDeletingId(null);
    }
  }

  function downloadCsv() {
    if (!sourceList.length) return;
    let header, rows;
    if (view === 'local_only') {
      header = ['site_number', 'site_name', 'company', 'city', 'province_state', 'pos_type', 'fuel_brand'];
      rows = visible.map((m) => header.map((h) => m[h] ?? ''));
    } else {
      header = ['unique_site_id', 'client_id', 'site_name', 'pos', 'city', 'province', 'ci_si', 'in_command', 'match'];
      rows = visible.map((m) => header.map((h) => m[h] ?? ''));
    }
    const esc = (v) => /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v);
    const lines = [header.join(',')].concat(rows.map((r) => r.map(esc).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `billing-diff-${view}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <h2>Billing Report Diff</h2>
          <div className="subtitle">
            Click a tile to switch views. Matching falls back to digits when
            the alpha prefix differs (e.g. billing "ess50050" matches local
            "pki50050") — fuzzy matches get a yellow "fuzzy" badge.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button type="button" className="btn-secondary" onClick={downloadCsv} disabled={!data}>
            Download CSV
          </button>
        </div>
      </div>

      {loading && <p>Loading…</p>}
      {error && <p className="error">{error}</p>}

      {data && (
        <>
          <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-3)', flexWrap: 'wrap' }}>
            <Tile label="In Billing Report"    value={data.billing_total}    tone="#0e7490"
              active={view === 'billing_all'} onClick={() => setView('billing_all')} />
            <Tile label="Active in Command"    value={data.local_total}      tone="#16a34a"
              active={view === 'local_only'}  onClick={() => setView('local_only')} />
            <Tile label="Missing from Command" value={data.missing_total}    tone="#b91c1c"
              active={view === 'missing'}     onClick={() => setView('missing')} />
          </div>

          {data.digit_match_count > 0 && (
            <div style={{
              marginBottom: 'var(--space-3)',
              padding: '8px 14px',
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-small)',
              color: '#92400e'
            }}>
              <strong>{data.digit_match_count}</strong> rows matched by digits (different alpha prefix on each side — most often billing using "ess…" while Command uses "pki…" for the same site).
            </div>
          )}

          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
            <input
              type="search"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ minWidth: 280 }}
            />
            <span className="result-count">
              {view === 'missing'     && `${visible.length} of ${data.missing_total} missing`}
              {view === 'billing_all' && `${visible.length} of ${data.billing_total} billing rows`}
              {view === 'local_only'  && `${visible.length} of ${data.local_only_total} local-only sites`}
            </span>
          </div>

          <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)', marginBottom: 'var(--space-3)' }}>
            {view === 'missing' && (
              <>Sites in the 2026 billing report that <strong>are not Active</strong> in Command. Either add them or reactivate the local site if it exists.</>
            )}
            {view === 'billing_all' && (
              <>Every site in the 2026 billing report (324 total). The "In Command" column shows whether each matches an Active Command site.</>
            )}
            {view === 'local_only' && (
              <>Active sites in Command that <strong>are not in the 2026 billing report</strong>. Either xsiteio isn't billing for them yet, or they need to be added on the xsiteio side.</>
            )}
          </div>

          {visible.length === 0 && (
            <p className="panel-empty">
              {sourceList.length === 0 ? 'Nothing to show for this view.' : 'No matches for the current search.'}
            </p>
          )}

          {visible.length > 0 && view !== 'local_only' && (
            <div className="panel" style={{ overflow: 'hidden', padding: 0 }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Site #</th>
                    <th style={{ textAlign: 'left' }}>Site Name</th>
                    <th style={{ textAlign: 'left' }}>Client / Org</th>
                    <th style={{ textAlign: 'left' }}>City</th>
                    <th style={{ textAlign: 'left' }}>Prov</th>
                    <th style={{ textAlign: 'left' }}>POS</th>
                    {view === 'billing_all' && <th style={{ textAlign: 'left' }}>In Command</th>}
                    <th style={{ textAlign: 'right', width: 140 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((m) => (
                    <tr key={m.unique_site_id}>
                      <td className="cell-mono">{m.unique_site_id}</td>
                      <td>{m.site_name}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-caption)' }}>{m.client_id}</td>
                      <td>{m.city}</td>
                      <td>{m.province}</td>
                      <td><PosBadge pos={m.pos} /></td>
                      {view === 'billing_all' && (
                        <td>
                          {m.in_command ? (
                            <span style={{ color: 'var(--color-good, #047857)', fontWeight: 'var(--font-semibold)' }}>
                              ✓ Yes
                              {m.match === 'digits' && <FuzzyBadge localSiteNumber={m.local_site_number} />}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-bad, #b91c1c)', fontWeight: 'var(--font-semibold)' }}>✗ No</span>
                          )}
                        </td>
                      )}
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {(view === 'missing' || (view === 'billing_all' && !m.in_command)) && (
                          <>
                            <Link
                              to={`/customers/new?site_number=${encodeURIComponent(m.unique_site_id)}&company=${encodeURIComponent(m.client_id)}&site_name=${encodeURIComponent(m.site_name)}&city=${encodeURIComponent(m.city)}&province_state=${encodeURIComponent(m.province)}&pos=${encodeURIComponent(m.pos || '')}&phone=${encodeURIComponent(m.phone || '')}&email=${encodeURIComponent(m.email || '')}&address=${encodeURIComponent(m.address || '')}&customer_since=${encodeURIComponent(m.customer_since || '')}`}
                              className="btn-secondary btn-sm"
                              style={{ marginRight: 6 }}
                            >+ Add</Link>
                            <button
                              type="button"
                              className="btn-danger btn-sm"
                              onClick={() => handleDeleteBillingRow(m)}
                              disabled={deletingId === m.unique_site_id}
                              title={`Remove "${m.site_name}" from the billing report (server/data/billing-report-2026.json)`}
                            >
                              {deletingId === m.unique_site_id ? 'Deleting…' : 'Delete'}
                            </button>
                          </>
                        )}
                        {view === 'billing_all' && m.in_command && m.match === 'digits' && m.local_customer_id && (
                          <Link to={`/customers/${m.local_customer_id}`} className="btn-secondary btn-sm">
                            Open Local
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {visible.length > 0 && view === 'local_only' && (
            <div className="panel" style={{ overflow: 'hidden', padding: 0 }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Site #</th>
                    <th style={{ textAlign: 'left' }}>Site Name</th>
                    <th style={{ textAlign: 'left' }}>Customer</th>
                    <th style={{ textAlign: 'left' }}>City</th>
                    <th style={{ textAlign: 'left' }}>Prov</th>
                    <th style={{ textAlign: 'left' }}>POS</th>
                    <th style={{ textAlign: 'left' }}>Fuel Brand</th>
                    <th style={{ textAlign: 'right', width: 140 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((s) => (
                    <tr key={s.site_id}>
                      <td className="cell-mono">{s.site_number}</td>
                      <td>{s.site_name}</td>
                      <td>{s.company || '—'}</td>
                      <td>{s.city || '—'}</td>
                      <td>{s.province_state || '—'}</td>
                      <td><PosBadge pos={s.pos_type} /></td>
                      <td>{s.fuel_brand || '—'}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Link to={`/customers/${s.customer_id}`} className="btn-secondary btn-sm" style={{ marginRight: 6 }}>
                          Open
                        </Link>
                        <button
                          type="button"
                          className="btn-danger btn-sm"
                          onClick={() => handleDelete(s)}
                          disabled={deletingId === s.site_id}
                          title={`Delete ${s.site_name || s.site_number} from Command`}
                        >
                          {deletingId === s.site_id ? 'Deleting…' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
